/**
 * PrivateRoute — protects routes behind authentication + optional role check.
 * Also handles the mustChangePassword redirect.
 */

import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import type { UserRole } from "@/types";

interface PrivateRouteProps {
  roles?: UserRole[];
}

export default function PrivateRoute({ roles }: PrivateRouteProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const refreshCurrentUser = useAuthStore((s) => s.refreshCurrentUser);
  const location = useLocation();
  const [isRepairingSession, setIsRepairingSession] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || user?.role) {
      setIsRepairingSession(false);
      return;
    }
    let cancelled = false;
    setIsRepairingSession(true);
    refreshCurrentUser().finally(() => {
      if (!cancelled) setIsRepairingSession(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, refreshCurrentUser, user?.role]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user?.role || isRepairingSession) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const isOnChangePasswordPage = location.pathname === "/change-password";
  if (user.mustChangePassword && !isOnChangePasswordPage) {
    return <Navigate to="/change-password" replace />;
  }
  if (isOnChangePasswordPage && !user.mustChangePassword) {
    return <Navigate to="/" replace />;
  }
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
